import { useEffect, useState } from 'react';
import {
  HANDOVER_STATUSES,
  HANDOVER_STATUS_LABELS,
  type HandoverStatus,
} from '@cannery/shared';
import { useAuth } from '../../auth/AuthContext';
import { api, ApiError } from '../../lib/api';
import { Badge, Button, Card, ErrorBanner, Modal, Spinner, inputCls } from '../../components/ui';
import { Icon } from '../../components/Icon';

/**
 * Phase 2G.8 — Daily Operations & Shift Handover Accountability.
 *
 * An administrator-recorded handover of UNFINISHED WORK for one branch
 * and one Asia/Manila business day:
 *
 *   DRAFT      - notes only; nothing is snapshotted yet.
 *   SUBMITTED  - the server captured an immutable snapshot of every
 *                active transaction (status, open/completed stages,
 *                item accountability, delivery state, factual exception
 *                categories) at this exact moment.
 *   ACCEPTED   - the recipient attested, optionally noting unresolved
 *                items; the submitted snapshot is never rewritten.
 *
 * Honesty rules baked into this view: the system stores NO shifts or
 * clock times (this is an operational handover, not attendance); the
 * snapshot is immutable and the CURRENT state of each transaction is
 * shown side by side with what was documented ("changed after handover"
 * is a factual comparison); unresolved work (open stage / item-count
 * discrepancy) stays visible forever and is never silently accepted
 * away; no customer personal information is copied into handovers; the
 * client never derives a snapshot - every figure below comes from the
 * server.
 */

interface HandoverActor {
  id: string | null;
  name: string;
}

interface HandoverDto {
  id: string;
  reference: string;
  branch: { id: string; code: string };
  businessDate: string;
  status: HandoverStatus;
  statusLabel: string;
  notes: string | null;
  acceptanceNotes: string | null;
  preparedBy: HandoverActor | null;
  submittedBy: HandoverActor | null;
  submittedAt: string | null;
  acceptedBy: HandoverActor | null;
  acceptedAt: string | null;
  itemCount: number;
  unresolvedCount: number;
  createdAt: string;
  updatedAt: string;
}

interface HandoverCurrentState {
  status: string;
  statusLabel: string;
  openStage: string | null;
  unresolved: boolean;
  lastActorName: string | null;
  lastActivityAt: string;
  statusChanged: boolean;
  openStageChanged: boolean;
  changedSinceHandover: boolean;
}

interface SnapshotDto {
  id: string;
  transactionId: string;
  transactionNumber: string;
  fulfillmentMethod: string;
  status: string;
  statusLabel: string;
  deliveryStatus: string | null;
  deliveryAssignee: string | null;
  openStage: string | null;
  openStageLabel: string | null;
  openStageMachine: string | null;
  openStageStartedAt: string | null;
  openStageEmployeeName: string | null;
  lastCompletedStage: string | null;
  lastCompletedLabel: string | null;
  lastCompletedAt: string | null;
  lastActorName: string | null;
  lastActorRole: string | null;
  lastActivityAt: string;
  expectedItemCount: number | null;
  lastActualCount: number | null;
  itemCountDifference: number | null;
  exceptionCategories: string[];
  flagLabels: string[];
  unresolved: boolean;
  current: HandoverCurrentState | null;
}

interface HistoryEntry {
  action: string;
  label: string;
  actorName: string;
  actorRole: string;
  metadata: unknown;
  createdAt: string;
}

interface DetailResponse {
  timeZone: string;
  handover: HandoverDto;
  snapshots: SnapshotDto[];
  history: HistoryEntry[];
}

interface ListResponse {
  total: number;
  limit: number;
  offset: number;
  timeZone: string;
  range: { from: string | null; to: string | null } | null;
  status: HandoverStatus | null;
  items: HandoverDto[];
}

const PAGE_SIZE = 20;

const STATUS_TONE: Record<HandoverStatus, 'slate' | 'indigo' | 'emerald'> = {
  DRAFT: 'slate',
  SUBMITTED: 'indigo',
  ACCEPTED: 'emerald',
};

function StatusBadge({ status }: { status: HandoverStatus }) {
  return <Badge tone={STATUS_TONE[status]}>{HANDOVER_STATUS_LABELS[status]}</Badge>;
}

function formatWhen(iso: string): string {
  return new Date(iso).toLocaleString('en-PH', { dateStyle: 'medium', timeStyle: 'short' });
}

/** Manila calendar date for shortcuts (the server re-parses dates as Manila). */
function manilaToday(): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Manila' }).format(new Date());
}

const NOT_RECORDED = <span className="italic text-slate-400">Not recorded</span>;

/** Signed item-count difference display (negative = fewer items than expected). */
function DifferenceCell({ cents }: { cents: number | null }) {
  if (cents === null) return <span className="text-slate-400">—</span>;
  if (cents === 0) return <span className="tabular-nums text-slate-500">Match</span>;
  return (
    <span
      className="font-semibold tabular-nums text-rose-700"
      title="Recorded actual count differs from the expected count"
    >
      {cents > 0 ? `+${cents}` : cents} item{Math.abs(cents) === 1 ? '' : 's'}
    </span>
  );
}

function UnresolvedBadge({ unresolved }: { unresolved: boolean }) {
  return unresolved ? (
    <Badge tone="amber">Unresolved items</Badge>
  ) : (
    <span className="text-slate-400">—</span>
  );
}

interface FormState {
  mode: 'create' | 'edit';
  id?: string;
  branch: string;
  businessDate: string;
  notes: string;
}

export function HandoverPage() {
  const { branchFilter, setBranchFilter, branches } = useAuth();

  const [list, setList] = useState<ListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  // View filters — every one is re-validated server-side on each request.
  const [statusFilter, setStatusFilter] = useState<'' | HandoverStatus>('');
  const [fromFilter, setFromFilter] = useState('');
  const [toFilter, setToFilter] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [qFilter, setQFilter] = useState('');
  const [page, setPage] = useState(0);

  // Detail modal (+ snapshot comparison, history and actions).
  const [detailId, setDetailId] = useState<string | null>(null);
  const [detail, setDetail] = useState<DetailResponse | null>(null);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [actionBusy, setActionBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [confirmSubmit, setConfirmSubmit] = useState(false);

  // Accept dialog (recipient attestation + optional notes).
  const [acceptOpen, setAcceptOpen] = useState(false);
  const [acceptanceNotes, setAcceptanceNotes] = useState('');

  // Create / edit-draft form (notes only - snapshots happen at submit).
  const [form, setForm] = useState<FormState | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [formBusy, setFormBusy] = useState(false);

  const scopeParams = () => {
    const params = new URLSearchParams();
    if (branchFilter !== 'ALL') params.set('branch', branchFilter);
    if (fromFilter) params.set('from', fromFilter);
    if (toFilter) params.set('to', toFilter);
    return params;
  };

  // List (paginated, deterministic: business date desc, reference desc).
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const params = scopeParams();
    if (statusFilter) params.set('status', statusFilter);
    if (qFilter) params.set('q', qFilter);
    params.set('limit', String(PAGE_SIZE));
    params.set('offset', String(page * PAGE_SIZE));
    void api
      .get<ListResponse>(`/handovers?${params.toString()}`)
      .then((res) => {
        if (!cancelled) {
          setList(res);
          setError(null);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof ApiError ? err.message : 'Failed to load handovers.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchFilter, fromFilter, toFilter, statusFilter, qFilter, page, refreshKey]);

  // Detail (fetched only while its modal is open).
  useEffect(() => {
    if (!detailId) {
      setDetail(null);
      setDetailError(null);
      setActionError(null);
      setConfirmSubmit(false);
      setAcceptOpen(false);
      setAcceptanceNotes('');
      return;
    }
    let cancelled = false;
    setDetail(null);
    setDetailError(null);
    const params = new URLSearchParams();
    if (branchFilter !== 'ALL') params.set('branch', branchFilter);
    void api
      .get<DetailResponse>(`/handovers/${detailId}?${params.toString()}`)
      .then((res) => {
        if (!cancelled) setDetail(res);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setDetailError(err instanceof ApiError ? err.message : 'Failed to load the handover.');
        }
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detailId, branchFilter, refreshKey]);

  const refresh = () => setRefreshKey((k) => k + 1);

  const resetPageAnd = (fn: () => void) => {
    setPage(0);
    fn();
  };

  const scopeLabel = branchFilter === 'ALL' ? 'All branches' : `Branch ${branchFilter}`;
  const total = list?.total ?? 0;
  const showingFrom = total === 0 ? 0 : page * PAGE_SIZE + 1;
  const showingTo = Math.min((page + 1) * PAGE_SIZE, total);
  const hasFilters = Boolean(statusFilter || qFilter || fromFilter || toFilter);

  const openCreate = () => {
    setFormError(null);
    setForm({
      mode: 'create',
      branch: branchFilter === 'ALL' ? (branches[0]?.code ?? '') : branchFilter,
      businessDate: manilaToday(),
      notes: '',
    });
  };

  const openEdit = (row: HandoverDto) => {
    setFormError(null);
    setForm({
      mode: 'edit',
      id: row.id,
      branch: row.branch.code,
      businessDate: row.businessDate,
      notes: row.notes ?? '',
    });
  };

  /** Shared async action wrapper: busy flag, error surface, list+detail refresh on success. */
  const runAction = async (action: () => Promise<void>) => {
    setActionBusy(true);
    setActionError(null);
    try {
      await action();
      refresh();
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : 'The action failed.');
    } finally {
      setActionBusy(false);
    }
  };

  const submitDraft = () =>
    runAction(async () => {
      await api.post(`/handovers/${detailId}/submit`);
      setConfirmSubmit(false);
    });

  const acceptHandover = () =>
    runAction(async () => {
      await api.post(`/handovers/${detailId}/accept`, {
        acceptanceNotes: acceptanceNotes.trim() || undefined,
      });
      setAcceptOpen(false);
      setAcceptanceNotes('');
    });

  /** Create a draft or correct draft notes (server validates and derives everything else). */
  const saveForm = () => {
    if (!form) return;
    setFormBusy(true);
    setFormError(null);
    const request =
      form.mode === 'create'
        ? api.post<{ handover: HandoverDto }>('/handovers', {
            branch: form.branch,
            businessDate: form.businessDate,
            notes: form.notes.trim() || undefined,
          })
        : api.patch<{ handover: HandoverDto }>(`/handovers/${form.id}`, {
            notes: form.notes.trim() || null,
          });
    void request
      .then((res) => {
        setForm(null);
        setDetailId(res.handover.id);
        refresh();
      })
      .catch((err: unknown) => {
        setFormError(err instanceof ApiError ? err.message : 'Failed to save the handover.');
      })
      .finally(() => setFormBusy(false));
  };

  return (
    <section className="space-y-4" aria-label="Operational handover">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900">
            Operational Handover
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            {scopeLabel} · unfinished work moving between people — an immutable snapshot of every
            in-progress transaction at submission, with the current state shown alongside.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={refresh}>
            <Icon name="refresh" className="h-5 w-5" />
            Refresh
          </Button>
          <Button onClick={openCreate}>
            <Icon name="plus" className="h-5 w-5" />
            New handover
          </Button>
        </div>
      </div>

      <Card className="flex items-start gap-3 p-4">
        <Icon name="shield" className="mt-0.5 h-5 w-5 shrink-0 text-slate-400" />
        <p className="text-sm text-slate-500">
          This records <span className="font-semibold text-slate-700">who handed work over and who
          accepted it</span> — the system stores no shifts, schedules or clock-in times, so this is
          an operational handover, not attendance tracking. On submission the server snapshots every
          in-progress transaction of the branch{' '}
          <span className="font-semibold text-slate-700">exactly as recorded then</span> (status,
          open and completed stages, item counts, delivery state, exception categories) — that
          snapshot is immutable, and later progress is shown next to it as a factual comparison.
          No customer personal information is copied into handovers, and unresolved work (an open
          stage or an item-count discrepancy) stays visible — acceptance never erases it.
        </p>
      </Card>

      {error && <ErrorBanner message={error} />}

      {/* Filters — branch mirrors the header switcher; the rest re-validated server-side. */}
      <Card className="flex flex-wrap items-end gap-3 p-4">
        <label className="flex min-w-44 flex-col gap-1 text-xs font-semibold text-slate-500">
          Branch
          <select
            value={branchFilter}
            onChange={(e) => resetPageAnd(() => setBranchFilter(e.target.value))}
            className={inputCls}
          >
            <option value="ALL">All Branches</option>
            {branches.map((branch) => (
              <option key={branch.id} value={branch.code}>
                {branch.code} — {branch.name}
              </option>
            ))}
          </select>
        </label>
        <label className="flex min-w-44 flex-col gap-1 text-xs font-semibold text-slate-500">
          Status
          <select
            value={statusFilter}
            onChange={(e) =>
              resetPageAnd(() => setStatusFilter(e.target.value as '' | HandoverStatus))
            }
            className={inputCls}
          >
            <option value="">All statuses</option>
            {HANDOVER_STATUSES.map((status) => (
              <option key={status} value={status}>
                {HANDOVER_STATUS_LABELS[status]}
              </option>
            ))}
          </select>
        </label>
        <label className="flex min-w-40 flex-col gap-1 text-xs font-semibold text-slate-500">
          From (business date)
          <input
            type="date"
            value={fromFilter}
            onChange={(e) => resetPageAnd(() => setFromFilter(e.target.value))}
            className={inputCls}
          />
        </label>
        <label className="flex min-w-40 flex-col gap-1 text-xs font-semibold text-slate-500">
          To (business date)
          <input
            type="date"
            value={toFilter}
            onChange={(e) => resetPageAnd(() => setToFilter(e.target.value))}
            className={inputCls}
          />
        </label>
        <Button
          variant="secondary"
          onClick={() =>
            resetPageAnd(() => {
              setFromFilter(manilaToday());
              setToFilter(manilaToday());
            })
          }
        >
          Today
        </Button>
        <form
          className="flex items-end gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            resetPageAnd(() => setQFilter(searchInput.trim()));
          }}
        >
          <label className="flex min-w-56 flex-col gap-1 text-xs font-semibold text-slate-500">
            Reference or transaction
            <input
              type="search"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="HOF-LH1-000001, LH1-000004…"
              className={inputCls}
            />
          </label>
          <Button variant="secondary" type="submit">
            <Icon name="search" className="h-5 w-5" />
            Search
          </Button>
        </form>
        {hasFilters && (
          <Button
            variant="secondary"
            onClick={() =>
              resetPageAnd(() => {
                setStatusFilter('');
                setFromFilter('');
                setToFilter('');
                setSearchInput('');
                setQFilter('');
              })
            }
          >
            Clear filters
          </Button>
        )}
        <span className="ml-auto text-xs font-medium text-slate-400">
          Dates are Manila business dates (Asia/Manila){list?.range
            ? ` · ${list.range.from ?? '…'} → ${list.range.to ?? '…'}`
            : ''}
        </span>
      </Card>

      {/* Handover list. */}
      <div>
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h2 className="text-sm font-semibold text-slate-700">Handovers</h2>
          {list && total > 0 && (
            <p className="text-sm text-slate-500">
              Showing {showingFrom}–{showingTo} of {total} handover{total === 1 ? '' : 's'} ·
              newest business date first
            </p>
          )}
        </div>

        {loading && !list ? (
          <div className="flex items-center gap-3 py-6 text-sm text-slate-500">
            <Spinner className="h-5 w-5 text-indigo-600" /> Loading handovers…
          </div>
        ) : !list || list.items.length === 0 ? (
          <Card className="mt-2 flex flex-col items-center p-6 text-center">
            <Icon name="check" className="h-8 w-8 text-emerald-400" />
            <p className="mt-2 font-medium text-slate-700">No handovers match these filters.</p>
            <p className="mt-1 text-sm text-slate-500">
              {hasFilters
                ? 'Clear a filter to widen the search.'
                : 'Create one with “New handover” to document unfinished work moving to the next person.'}
            </p>
          </Card>
        ) : (
          <>
            <div className="mt-2 overflow-x-auto rounded-2xl border border-slate-200 bg-panel shadow-sm">
              <table className="w-full text-left text-sm">
                <thead className="bg-slate-50 text-xs font-semibold tracking-wide text-slate-500 uppercase">
                  <tr>
                    <th className="px-4 py-3">Reference</th>
                    <th className="px-4 py-3">Branch</th>
                    <th className="px-4 py-3">Business date</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-3 py-3 text-right">Items</th>
                    <th className="px-4 py-3">Unresolved at submit</th>
                    <th className="px-4 py-3">Prepared by</th>
                    <th className="px-4 py-3">Submitted</th>
                    <th className="px-4 py-3">Accepted</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {list.items.map((row) => (
                    <tr key={row.id} className="align-top hover:bg-slate-100/70">
                      <td className="px-4 py-3">
                        <button
                          type="button"
                          onClick={() => setDetailId(row.id)}
                          className="font-semibold text-indigo-600 underline-offset-2 hover:underline"
                          title="Open handover detail"
                        >
                          {row.reference}
                        </button>
                      </td>
                      <td className="px-4 py-3">
                        <Badge>{row.branch.code}</Badge>
                      </td>
                      <td className="px-4 py-3 text-slate-600">{row.businessDate}</td>
                      <td className="px-4 py-3">
                        <StatusBadge status={row.status} />
                      </td>
                      <td className="px-3 py-3 text-right tabular-nums text-slate-700">
                        {row.status === 'DRAFT' ? (
                          <span className="text-slate-400">—</span>
                        ) : (
                          row.itemCount
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {row.status === 'DRAFT' ? (
                          <span className="text-slate-400">—</span>
                        ) : row.unresolvedCount > 0 ? (
                          <Badge tone="amber">
                            {row.unresolvedCount} of {row.itemCount}
                          </Badge>
                        ) : (
                          <span className="text-slate-400">None</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-slate-600">
                        {row.preparedBy ? row.preparedBy.name : NOT_RECORDED}
                      </td>
                      <td className="px-4 py-3 text-slate-600">
                        {row.submittedAt ? (
                          <>
                            <span className="block">{formatWhen(row.submittedAt)}</span>
                            <span className="mt-0.5 block text-xs text-slate-500">
                              {row.submittedBy?.name}
                            </span>
                          </>
                        ) : (
                          <span className="italic text-slate-400">Not submitted</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-slate-600">
                        {row.acceptedAt ? (
                          <>
                            <span className="block">{formatWhen(row.acceptedAt)}</span>
                            <span className="mt-0.5 block text-xs text-slate-500">
                              {row.acceptedBy?.name}
                            </span>
                          </>
                        ) : (
                          <span className="italic text-slate-400">Not accepted</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>


            {total > PAGE_SIZE && (
              <div className="mt-3 flex items-center justify-between">
                <Button
                  variant="secondary"
                  disabled={page === 0}
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                >
                  Previous
                </Button>
                <span className="text-sm text-slate-500">
                  Page {page + 1} of {Math.max(1, Math.ceil(total / PAGE_SIZE))}
                </span>
                <Button
                  variant="secondary"
                  disabled={(page + 1) * PAGE_SIZE >= total}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next
                </Button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Detail: immutable snapshot vs current state, attribution, history, actions. */}
      {detailId && !detail && !detailError && (
        <Modal title="Handover" onClose={() => setDetailId(null)}>
          <div className="flex items-center justify-center gap-3 py-8 text-sm text-slate-500">
            <Spinner className="h-5 w-5 text-indigo-600" /> Loading handover…
          </div>
        </Modal>
      )}
      {detailId && detailError && (
        <Modal title="Handover" onClose={() => setDetailId(null)}>
          <ErrorBanner message={detailError} />
        </Modal>
      )}
      {detailId && detail &&
        (() => {
          const row = detail.handover;
          return (
            <Modal
              title={`Handover — ${row.reference}`}
              onClose={() => setDetailId(null)}
              footer={
                <>
                  {row.status === 'DRAFT' && (
                    <>
                      <Button variant="secondary" onClick={() => openEdit(row)}>
                        Edit notes
                      </Button>
                      <Button disabled={actionBusy} onClick={() => setConfirmSubmit(true)}>
                        Submit handover
                      </Button>
                    </>
                  )}
                  {row.status === 'SUBMITTED' && (
                    <Button disabled={actionBusy} onClick={() => setAcceptOpen(true)}>
                      Accept handover
                    </Button>
                  )}
                  {row.status === 'ACCEPTED' && (
                    <span className="text-sm text-slate-500">
                      Accepted — record frozen (the snapshot is never rewritten)
                    </span>
                  )}
                </>
              }
            >
              <div className="space-y-4">
                {actionError && <ErrorBanner message={actionError} />}

                <div className="flex flex-wrap items-center gap-2 text-sm text-slate-600">
                  <StatusBadge status={row.status} />
                  <Badge>{row.branch.code}</Badge>
                  <span>
                    {row.businessDate} (Asia/Manila)
                  </span>
                  <span className="text-slate-400">
                    {row.status === 'DRAFT'
                      ? 'draft — no snapshot captured yet'
                      : `snapshot captured at submission — ${row.itemCount} transaction${row.itemCount === 1 ? '' : 's'}`}
                  </span>
                </div>

                <div className="space-y-2 text-sm">
                  <p className="text-slate-600">
                    Prepared by{' '}
                    <span className="font-medium text-slate-800">
                      {row.preparedBy?.name ?? 'Not recorded'}
                    </span>
                    {row.submittedAt ? (
                      <>
                        {' '}· submitted {formatWhen(row.submittedAt)} by{' '}
                        <span className="font-medium text-slate-800">
                          {row.submittedBy?.name ?? 'Not recorded'}
                        </span>
                      </>
                    ) : (
                      ' · not submitted'
                    )}
                    {row.acceptedAt ? (
                      <>
                        {' '}· accepted {formatWhen(row.acceptedAt)} by{' '}
                        <span className="font-medium text-slate-800">
                          {row.acceptedBy?.name ?? 'Not recorded'}
                        </span>
                      </>
                    ) : (
                      ' · not accepted'
                    )}
                  </p>
                  <p className="text-slate-500">
                    <span className="font-medium text-slate-700">Preparer notes:</span>{' '}
                    {row.notes || <span className="italic text-slate-400">None</span>}
                  </p>
                  {row.acceptanceNotes && (
                    <p className="text-slate-500">
                      <span className="font-medium text-slate-700">Acceptance notes (unresolved
                      items):</span>{' '}
                      {row.acceptanceNotes}
                    </p>
                  )}
                </div>

                {detail.snapshots.length === 0 ? (
                  <Card className="flex flex-col items-center p-5 text-center">
                    <Icon name="check" className="h-7 w-7 text-emerald-400" />
                    <p className="mt-2 text-sm font-medium text-slate-700">
                      Draft — nothing snapshotted yet.
                    </p>
                    <p className="mt-1 text-xs text-slate-500">
                      Submitting captures every in-progress transaction of this branch at that
                      moment (an empty branch cannot be submitted).
                    </p>
                  </Card>
                ) : (
                  <div>
                    <div className="flex flex-wrap items-baseline justify-between gap-2">
                      <h3 className="text-sm font-semibold text-slate-700">
                        Snapshot at submission vs current state
                      </h3>
                      <p className="text-xs text-slate-400">
                        Left of “vs” is immutable; right side is live
                      </p>
                    </div>
                    <div className="mt-2 overflow-x-auto rounded-xl border border-slate-200">
                      <table className="w-full text-left text-sm">
                        <thead className="bg-slate-50 text-xs font-semibold tracking-wide text-slate-500 uppercase">
                          <tr>
                            <th className="px-3 py-2">Transaction</th>
                            <th className="px-3 py-2">At handover</th>
                            <th className="px-3 py-2">Open stage then</th>
                            <th className="px-3 py-2">Items</th>
                            <th className="px-3 py-2">Flags</th>
                            <th className="px-3 py-2">Current state</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {detail.snapshots.map((snap) => (
                            <tr key={snap.id} className="align-top">
                              <td className="px-3 py-2">
                                <span className="font-semibold text-slate-800">
                                  {snap.transactionNumber}
                                </span>
                                <p className="mt-0.5 text-xs text-slate-500">
                                  {snap.fulfillmentMethod === 'DELIVERY' ? 'Delivery' : 'Pick-up'}
                                  {snap.deliveryStatus
                                    ? ` · delivery ${snap.deliveryStatus.toLowerCase()}`
                                    : ''}
                                  {snap.deliveryAssignee ? ` · ${snap.deliveryAssignee}` : ''}
                                </p>
                              </td>
                              <td className="px-3 py-2 text-slate-700">
                                <Badge tone="indigo">{snap.statusLabel}</Badge>
                                <p className="mt-1 text-xs text-slate-500">
                                  last: {snap.lastActorName ?? 'Not recorded'}
                                  {snap.lastActorRole ? ` (${snap.lastActorRole})` : ''}
                                </p>
                                <p className="text-xs text-slate-400">
                                  {formatWhen(snap.lastActivityAt)}
                                </p>
                              </td>
                              <td className="px-3 py-2 text-slate-700">
                                {snap.openStage ? (
                                  <>
                                    <span className="font-medium">{snap.openStageLabel}</span>
                                    {snap.openStageMachine ? (
                                      <span className="text-slate-500"> · {snap.openStageMachine}</span>
                                    ) : null}
                                    <p className="text-xs text-slate-500">
                                      {snap.openStageEmployeeName ?? 'Not recorded'}
                                      {snap.openStageStartedAt
                                        ? ` since ${formatWhen(snap.openStageStartedAt)}`
                                        : ''}
                                    </p>
                                  </>
                                ) : (
                                  <span className="text-slate-400">None open</span>
                                )}
                                <p className="mt-1 text-xs text-slate-500">
                                  last completed:{' '}
                                  {snap.lastCompletedLabel
                                    ? `${snap.lastCompletedLabel}${snap.lastCompletedAt ? ` · ${formatWhen(snap.lastCompletedAt)}` : ''}`
                                    : ' none'}
                                </p>
                              </td>
                              <td className="px-3 py-2">
                                <span className="tabular-nums text-slate-700">
                                  {snap.lastActualCount ?? '—'} / {snap.expectedItemCount ?? '—'}
                                </span>
                                <p className="mt-0.5 text-xs">
                                  <DifferenceCell cents={snap.itemCountDifference} />
                                </p>
                              </td>
                              <td className="px-3 py-2">
                                <UnresolvedBadge unresolved={snap.unresolved} />
                                {snap.flagLabels.length > 0 && (
                                  <p className="mt-1 text-xs text-slate-500">
                                    {snap.flagLabels.join(', ')}
                                  </p>
                                )}
                                {snap.exceptionCategories.length > 0 && (
                                  <p className="mt-0.5 text-xs text-slate-400">
                                    exceptions: {snap.exceptionCategories.join(', ')}
                                  </p>
                                )}
                              </td>
                              <td className="px-3 py-2">
                                {snap.current ? (
                                  <>
                                    <Badge tone={snap.current.statusChanged ? 'amber' : 'slate'}>
                                      {snap.current.statusLabel}
                                    </Badge>
                                    <p className="mt-1 text-xs text-slate-500">
                                      {snap.current.openStage
                                        ? `open: ${snap.current.openStage}`
                                        : 'no open stage'}
                                    </p>
                                    <p
                                      className={`mt-0.5 text-xs ${
                                        snap.current.changedSinceHandover
                                          ? 'font-semibold text-amber-700'
                                          : 'text-slate-400'
                                      }`}
                                    >
                                      {snap.current.changedSinceHandover
                                        ? 'Changed after handover'
                                        : 'Unchanged since handover'}
                                    </p>
                                    {snap.current.unresolved && (
                                      <p className="text-xs text-amber-700">Still unresolved</p>
                                    )}
                                  </>
                                ) : (
                                  <span className="text-slate-400">—</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <p className="mt-1 text-xs text-slate-400">
                      Snapshots are append-only (a database trigger blocks edits) and carry no
                      customer personal information — the reference identifies the transaction.
                    </p>
                  </div>
                )}

                <div className="border-t border-slate-100 pt-3">
                  <h3 className="text-sm font-semibold text-slate-700">History</h3>
                  {detail.history.length === 0 ? (
                    <p className="mt-1 text-sm text-slate-500">No recorded state changes.</p>
                  ) : (
                    <ol className="mt-2 space-y-2">
                      {detail.history.map((entry, index) => (
                        <li
                          key={`${entry.action}-${index}`}
                          className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm"
                        >
                          <span className="font-medium text-slate-800">{entry.label}</span>
                          <span className="text-slate-500">
                            {' '}· {entry.actorName} ({entry.actorRole}) · {formatWhen(entry.createdAt)}
                          </span>
                        </li>
                      ))}
                    </ol>
                  )}
                  <p className="mt-1 text-xs text-slate-400">
                    Append-only audit trail — submitted and accepted records are never rewritten.
                  </p>
                </div>
              </div>
            </Modal>
          );
        })()}

      {/* Submit confirmation: captures the immutable snapshot. */}
      {confirmSubmit && detail && (
        <Modal
          title="Submit handover?"
          onClose={() => {
            if (!actionBusy) setConfirmSubmit(false);
          }}
          footer={
            <>
              <Button
                variant="secondary"
                disabled={actionBusy}
                onClick={() => setConfirmSubmit(false)}
              >
                Cancel
              </Button>
              <Button disabled={actionBusy} onClick={submitDraft}>
                {actionBusy ? 'Submitting…' : 'Confirm submit'}
              </Button>
            </>
          }
        >
          <div className="space-y-3 text-sm text-slate-600">
            {actionError && <ErrorBanner message={actionError} />}
            <p>
              Submitting captures an <span className="font-semibold">immutable snapshot</span> of
              every in-progress transaction of{' '}
              {detail.handover.branch.code} at this moment — status, open and completed stages, item
              counts, delivery state and exception categories. After submission the record can no
              longer be edited — only accepted — and later progress will appear next to the snapshot
              as a comparison, never rewriting it.
            </p>
            <p className="rounded-xl bg-slate-50 px-3 py-2">
              {detail.handover.branch.code} · {detail.handover.businessDate} · prepared by{' '}
              {detail.handover.preparedBy?.name ?? 'Not recorded'}
            </p>
            <p className="text-xs text-slate-400">
              A branch with no in-progress transactions cannot be submitted — there would be
              nothing to hand over.
            </p>
          </div>
        </Modal>
      )}

      {/* Accept dialog: recipient attestation; unresolved items stay visible. */}
      {acceptOpen && detail && (
        <Modal
          title="Accept handover"
          onClose={() => {
            if (!actionBusy) setAcceptOpen(false);
          }}
          footer={
            <>
              <Button
                variant="secondary"
                disabled={actionBusy}
                onClick={() => setAcceptOpen(false)}
              >
                Cancel
              </Button>
              <Button disabled={actionBusy} onClick={acceptHandover}>
                {actionBusy ? 'Accepting…' : 'Confirm acceptance'}
              </Button>
            </>
          }
        >
          <div className="space-y-3 text-sm text-slate-600">
            {actionError && <ErrorBanner message={actionError} />}
            <p>
              Accepting records your attestation with a server timestamp. The submitted snapshot
              stays exactly as documented — accepting does not resolve or hide unresolved items
              (open stages and item discrepancies remain visible).
            </p>
            {detail.snapshots.some((snap) => snap.unresolved) && (
              <p className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-amber-900">
                <span className="font-semibold">Unresolved items in this handover:{' '}</span>
                {detail.snapshots
                  .filter((snap) => snap.unresolved)
                  .map((snap) => snap.transactionNumber)
                  .join(', ')}
              </p>
            )}
            <label className="block">
              <span className="mb-1.5 block text-sm font-medium text-slate-700">
                Notes about unresolved items (optional)
              </span>
              <textarea
                value={acceptanceNotes}
                onChange={(e) => setAcceptanceNotes(e.target.value)}
                maxLength={500}
                rows={3}
                placeholder="Anything still open that you are accepting responsibility for…"
                className="w-full rounded-xl border border-slate-300 bg-panel px-3.5 py-2.5 text-sm text-slate-900 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100"
              />
            </label>
          </div>
        </Modal>
      )}

      {/* Create / edit-draft form (branch + date immutable after creation). */}
      {form && (
        <Modal
          title={form.mode === 'create' ? 'New handover' : 'Edit draft notes'}
          onClose={() => {
            if (!formBusy) setForm(null);
          }}
          footer={
            <>
              <Button variant="secondary" disabled={formBusy} onClick={() => setForm(null)}>
                Cancel
              </Button>
              <Button disabled={formBusy} onClick={saveForm}>
                {formBusy ? 'Saving…' : form.mode === 'create' ? 'Create draft' : 'Save changes'}
              </Button>
            </>
          }
        >
          <div className="space-y-4">
            {formError && <ErrorBanner message={formError} />}

            {form.mode === 'create' ? (
              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <span className="mb-1.5 block text-sm font-medium text-slate-700">Branch</span>
                  <select
                    value={form.branch}
                    onChange={(e) => setForm({ ...form, branch: e.target.value })}
                    className={inputCls}
                    disabled={branchFilter !== 'ALL'}
                  >
                    {(branchFilter !== 'ALL'
                      ? branches.filter((b) => b.code === branchFilter)
                      : branches
                    ).map((branch) => (
                      <option key={branch.id} value={branch.code}>
                        {branch.code} — {branch.name}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="block">
                  <span className="mb-1.5 block text-sm font-medium text-slate-700">
                    Business date (Asia/Manila)
                  </span>
                  <input
                    type="date"
                    value={form.businessDate}
                    max={manilaToday()}
                    onChange={(e) => setForm({ ...form, businessDate: e.target.value })}
                    className={inputCls}
                  />
                </label>
              </div>
            ) : (
              <p className="rounded-xl bg-slate-50 px-3 py-2 text-sm text-slate-600">
                {form.branch} · {form.businessDate} — branch, business date and reference are
                immutable.
              </p>
            )}

            <label className="block">
              <span className="mb-1.5 block text-sm font-medium text-slate-700">
                Notes {form.mode === 'create' ? '(optional)' : ''}
              </span>
              <textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                maxLength={500}
                rows={3}
                placeholder="Context for the next person — machines running, special orders…"
                className="w-full rounded-xl border border-slate-300 bg-panel px-3.5 py-2.5 text-sm text-slate-900 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100"
              />
            </label>
            <p className="text-xs text-slate-400">
              The list of transactions is NOT chosen here: submitting snapshots every in-progress
              transaction of the branch at that moment, derived server-side.
            </p>
          </div>
        </Modal>
      )}
    </section>
  );
}
